import { useState } from "react";
import { useMutation } from "lib/apollo/client";
import Icon from "hardcover-ui/components/Icon";
import faHeart from "icons/solid/heart.svg";
import faHeartOutline from "icons/regular/heart.svg";
import classNames from "lib/classNames";
import Button from "hardcover-ui/components/Button";
import LikeableType from "types/LikeableType";
import likeActivityMutation from "queries/likes/likeActivityMutation";
import unlikeActivityMutation from "queries/likes/unlikeActivityMutation";
import { RefreshIcon } from "@heroicons/react/outline";
import LoggedInItemLikes from "./LoggedInItemLikes";
import { useIsLiked } from "store/currentUser/currentUserSelector";
import { useDispatch } from "react-redux";
import { toggleLike } from "store/currentUser/currentUserSlice";

export interface Props {
  likeable: LikeableType;
  showLikes: boolean;
}

interface LikeResultType {
  likeResult: {
    likesCount: number;
  };
}
export default function LoggedInLikeButton({ likeable, showLikes }: Props) {
  const isLiked = useIsLiked(likeable);
  const dispatch = useDispatch();

  const [likesCount, setLikesCount] = useState(likeable.likesCount);
  const [like, { loading: liking }] =
    useMutation<LikeResultType>(likeActivityMutation);
  const [unlike, { loading: unliking }] = useMutation<LikeResultType>(
    unlikeActivityMutation
  );

  const likeActivity = () => {
    return like({
      variables: {
        likeableId: likeable.id,
        likeableType: likeable.objectType,
      },
    });
  };

  const unlikeActivity = () => {
    return unlike({
      variables: {
        likeableId: likeable.id,
        likeableType: likeable.objectType,
      },
    });
  };

  const toggleLikeState = (e): void => {
    e.preventDefault();
    const action = isLiked ? unlikeActivity : likeActivity;
    action().then((result) => {
      setLikesCount(result.data.likeResult.likesCount);
      dispatch(toggleLike({ likeable: likeable }));
    });
  };

  return (
    <div className="flex items-center gap-2" style={{ minHeight: "24px" }}>
      <Button
        aria-label="Like or Unlike"
        onClick={toggleLikeState}
        className={classNames(
          isLiked
            ? "text-red-500 hover:text-red-300 transition-colors"
            : "text-primary dark:text-gray-300 hover:text-red-500"
        )}
      >
        {liking || unliking ? (
          <RefreshIcon className="animate-spin-reverse w-5 h-5" />
        ) : (
          <Icon
            icon={isLiked ? faHeart : faHeartOutline}
            size="lg"
            className={classNames(
              isLiked
                ? "fill-red-500 hover:opacity-70 transition-opacity"
                : "fill-gray-700 dark:fill-gray-300 hover:fill-red-500 dark:hover:fill-red-500 hover:rotate-3 transition-all"
            )}
          />
        )}
      </Button>
      {likesCount > 0 ? (
        <LoggedInItemLikes
          currentUserLiked={isLiked}
          likeable={likeable}
          showLikes={showLikes}
          likesCount={likesCount}
        />
      ) : (
        <></>
      )}
    </div>
  );
}
